import { useState } from 'react'
import { PersonasList } from '@/components/PersonasList'
import { PersonaEditor } from '@/components/PersonaEditor'
import { Drafts } from '@/components/Drafts'
import { Dashboard } from '@/components/Dashboard'
import { Topology } from '@/components/Topology'
import { AuditLog } from '@/components/AuditLog'
import { AccessRoles } from '@/components/AccessRoles'
import { ProjectConfig } from '@/components/ProjectConfig'
import { Escalation } from '@/components/Escalation'
import { ReloadControl } from '@/components/ReloadControl'
import { ProfileScreen } from '@/components/ProfileScreen'

/**
 * Shell — routes between Dashboard / Personas / Editor / Topology / Drafts /
 * Audit / History / Catalog / Config / Access / Reload / Profile per the
 * locked design baseline §3 IA (Operate · Govern · Admin rail). MVP wires
 * the Personas list + Editor end-to-end; the rest are stubs filled in
 * follow-up implement runs.
 */
type RouteId =
  | 'dashboard' | 'personas' | 'topology'
  | 'drafts' | 'audit' | 'history'
  | 'catalog' | 'config' | 'access' | 'reload'
  | 'escalation' | 'profile'

interface NavItem { id: RouteId; label: string }
interface NavGroup { group: string; items: NavItem[] }

const NAV: NavGroup[] = [
  { group: 'Operate', items: [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'personas',  label: 'Personas' },
    { id: 'topology',  label: 'Topology' },
  ]},
  { group: 'Govern', items: [
    { id: 'drafts',  label: 'Drafts' },
    { id: 'audit',   label: 'Audit log' },
    { id: 'history', label: 'Version history' },
  ]},
  { group: 'Admin', items: [
    { id: 'catalog', label: 'Tool catalog' },
    { id: 'config',  label: 'Project config' },
    { id: 'access',  label: 'Access & roles' },
    { id: 'escalation', label: 'Escalation' },
    { id: 'reload',  label: 'Reload control' },
  ]},
]

export function Shell() {
  const [route, setRoute] = useState<RouteId>('personas')
  const [editing, setEditing] = useState<string | null>(null)

  let content
  if (editing) {
    content = <PersonaEditor role={editing} onClose={() => setEditing(null)} />
  } else if (route === 'dashboard') {
    content = <Dashboard />
  } else if (route === 'personas') {
    content = <PersonasList onOpen={(r) => setEditing(r)} />
  } else if (route === 'drafts') {
    content = <Drafts onEdit={(r) => setEditing(r)} />
  } else if (route === 'topology') {
    content = <Topology />
  } else if (route === 'audit') {
    content = <AuditLog />
  } else if (route === 'access') {
    content = <AccessRoles />
  } else if (route === 'config') {
    content = <ProjectConfig />
  } else if (route === 'escalation') {
    content = <Escalation />
  } else if (route === 'reload') {
    content = <ReloadControl />
  } else if (route === 'profile') {
    content = <ProfileScreen />
  } else {
    content = (
      <div className="page">
        <div className="page-head">
          <h1>{labelFor(route)}</h1>
          <p>This screen is implemented in a follow-up story phase.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="app">
      <div className="brand">
        <img className="brand-logo" src="/console/Fusion-Logo.png" alt="Fusion II logo" />Fusion II <span className="ver">Console</span>
      </div>
      <div className="topbar">
        <div className="crumbs">{editing ? <><span onClick={() => setEditing(null)} style={{ cursor: 'pointer' }}>Personas</span> › <b className="mono">{editing}</b></> : <b>{labelFor(route)}</b>}</div>
      </div>
      <nav className="nav">
        {NAV.map((g) => (
          <div key={g.group}>
            <div className="group-label">{g.group}</div>
            {g.items.map((it) => (
              <button
                key={it.id}
                className={`nav-item ${route === it.id ? 'active' : ''}`}
                onClick={() => { setEditing(null); setRoute(it.id) }}
              >{it.label}</button>
            ))}
          </div>
        ))}
      </nav>
      <main className="main">{content}</main>
    </div>
  )
}

function labelFor(id: RouteId): string {
  for (const g of NAV) for (const it of g.items) if (it.id === id) return it.label
  return id
}
